import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { roman } from "../art";
import type { DailyCardView } from "@psy/shared";

type Phase = "idle" | "activating" | "rotating" | "flipping" | "revealing" | "revealed";

const DURATIONS: Record<Exclude<Phase, "idle" | "revealed">, number> = {
  activating: 300,
  rotating: 800,
  flipping: 700,
  revealing: 500,
};

const MOODS: { id: "good" | "meh" | "hard"; label: string }[] = [
  { id: "good", label: "Хорошо" },
  { id: "meh", label: "Так себе" },
  { id: "hard", label: "Тяжело" },
];

interface Props {
  firstName: string | null;
  onOpenFull: () => void;
}

/** Карта дня: рубашка остаётся на месте, рядом раскрывается послание (звезда мерцает → лёгкий переворот-флёр → текст). */
export function DailyCardWidget({ firstName, onOpenFull }: Props) {
  const [card, setCard] = useState<DailyCardView | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [mood, setMood] = useState<"good" | "meh" | "hard" | null>(null);
  const [savingMood, setSavingMood] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    let alive = true;
    api.dailyCard().then(
      (r) => {
        if (!alive) return;
        setCard(r.card);
        setMood(r.card.mood);
        // Карта всегда начинается закрытой и открывается анимацией по нажатию — alreadyShown влияет только
        // на то, что показывается текст того же разбора, что уже выдавался сегодня (сервер не меняет его).
        // Предзагружаем картинку конкретного аркана, чтобы при открытии не было паузы на загрузку.
        new Image().src = `/assets/arcana/${r.card.number}.png`;
      },
      () => undefined,
    );
    return () => {
      alive = false;
      timers.current.forEach(clearTimeout);
    };
  }, []);

  const reducedMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const open = () => {
    if (!card || phase !== "idle") return;
    if (reducedMotion) {
      setPhase("revealed");
      return;
    }
    const sequence: Exclude<Phase, "idle" | "revealed">[] = ["activating", "rotating", "flipping", "revealing"];
    let elapsed = 0;
    sequence.forEach((p, i) => {
      const t = setTimeout(() => setPhase(p), elapsed);
      timers.current.push(t);
      elapsed += DURATIONS[p];
      if (i === sequence.length - 1) {
        const done = setTimeout(() => setPhase("revealed"), elapsed);
        timers.current.push(done);
      }
    });
    setPhase("activating");
  };

  const pickMood = async (m: "good" | "meh" | "hard") => {
    setMood(m);
    setSavingMood(true);
    try {
      await api.setDailyMood(m);
    } catch {
      // тихо — мнение пользователя важнее сбоя сети
    } finally {
      setSavingMood(false);
    }
  };

  if (!card) return null;

  const opened = phase !== "idle" && phase !== "activating" && phase !== "rotating";
  const sparkling = phase === "activating" || phase === "rotating";
  const spinning = phase === "flipping";
  const flashing = phase === "revealing";

  return (
    <div className="dailycard">
      <div className="dailycard-head">
        <span className="dailycard-title">КАРТА ДНЯ</span>
        {card.streak > 0 && <span className="dailycard-streak">✦ {card.streak} {card.streak === 1 ? "день" : "дня"} подряд</span>}
      </div>

      <button
        type="button"
        className={`dailycard-row${phase === "idle" ? " is-idle" : ""}`}
        onClick={open}
        disabled={phase !== "idle"}
        aria-label={opened ? card.headline : "Твоя карта ждёт. Нажми, чтобы открыть послание на сегодня."}
      >
        <span className={`dailycard-art${spinning ? " is-spinning" : ""}`}>
          <img
            src={opened ? `/assets/arcana/${card.number}.png` : "/assets/cards/daily-card-back.png"}
            alt=""
            className="dailycard-backimg"
          />
          {phase === "idle" && (
            <>
              <span className="dailycard-ambient a1" aria-hidden>✦</span>
              <span className="dailycard-ambient a2" aria-hidden>✦</span>
              <span className="dailycard-ambient a3" aria-hidden>✦</span>
            </>
          )}
          {sparkling && (
            <>
              <span className="dailycard-sparkle s1" aria-hidden>✦</span>
              <span className="dailycard-sparkle s2" aria-hidden>✦</span>
              <span className="dailycard-sparkle s3" aria-hidden>✦</span>
            </>
          )}
          {flashing && <span className="dailycard-flash" aria-hidden />}
        </span>

        {!opened && (
          <span className="dailycard-prompt">
            <b>Твоя карта ждёт</b>
            <span>Нажми на карту, чтобы открыть послание на сегодня.</span>
          </span>
        )}

        {opened && (
          <span className={`dailycard-message${phase === "revealing" ? " is-fadein" : ""}`}>
            <b>
              {roman(card.number)} · {card.name}
            </b>
            <span className="dailycard-text">
              {firstName ? `${firstName}, ` : ""}
              {card.summary}
            </span>
          </span>
        )}
      </button>

      {opened && (
        <div className="dailycard-foot">
          <button type="button" className="dailycard-full" onClick={onOpenFull}>
            Подробный разбор
          </button>

          <div className="dailycard-mood">
            <span>{mood ? "Записала. Завтра спрошу снова" : "Как ты сегодня?"}</span>
            <div className="dailycard-moodbtns">
              {MOODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={mood === m.id ? "on" : ""}
                  disabled={savingMood}
                  onClick={() => void pickMood(m.id)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
